/**
 * contributionService.ts
 *
 * Handles user-submitted venue additions, edit suggestions, and
 * ownership claims. ALL submissions go through the contributions
 * review queue. Admin reviews and approves/rejects via AdminPage.
 *
 * FLOW:
 *   User submits → contributions table (status: pending)
 *   Admin reviews new_venue    → approves → venue goes live in venues table
 *   Admin reviews suggest_edit → marks reviewed after applying manually
 *     (free-text suggestions have no reliable auto-apply path)
 *   Admin reviews claim_venue  → approves → contact linked permanently
 *     in venue_owners, separate from this queue
 *   Any flow → rejects → contribution marked rejected
 *
 * WHY THE OLD "DIRECT SAVE MODE" WAS REMOVED:
 * - It bypassed admin review entirely — any user could put any content live
 * - It called /api/scan (wrong endpoint — should be /api/scan-menu) so AI
 *   parsing silently failed on every submission anyway
 * - It used claude-sonnet (expensive) when haiku handles deal parsing fine
 * - Admin "Pending approvals" tab was only receiving edit suggestions, not
 *   new venue submissions — making it useless for its primary purpose
 *
 * WHY claim_venue IS ITS OWN FLOW (added later):
 * - Ownership claims were previously submitted under flow:'suggest_edit'
 *   because that was the only tag available — there was no ClaimSubmission
 *   type. This meant every claim showed up in admin mislabeled as an
 *   "Edit suggestion" with no way to approve it (approval only existed for
 *   new_venue). AdminPage.tsx now also heuristically detects legacy
 *   suggest_edit rows that carry owner_name/owner_email as claims, so
 *   historical submissions aren't stranded by this fix.
 */

import { supabase } from '../lib/supabase'
import { Analytics } from './analytics'

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export type ContributionFlow = 'new_venue' | 'suggest_edit' | 'claim_venue'

export interface NewVenueSubmission {
  flow: 'new_venue'
  name: string
  address: string
  neighborhood: string
  city: string
  website?: string
  phone?: string
  latitude?: number
  longitude?: number
  dog_friendly?: boolean
  schedule_description: string
  deal_details: string
  schedules?: ScheduleSubmission[]
  notes?: string
  submitter_email?: string
}

export interface ScheduleSubmission {
  days: string[]
  start_time: string
  end_time: string
  is_all_day: boolean
  deal_text: string
  deals: DealSubmission[]
}

export interface DealSubmission {
  type: string
  description: string
  price?: number | null
}

export interface EditSuggestion {
  flow: 'suggest_edit'
  venue_id: string
  venue_name: string
  field_suggestions: string
  new_schedule?: string
  new_deal_details?: string
  notes?: string
  submitter_email?: string
}

// "I run this bar" — distinct from EditSuggestion ("this fact is wrong").
// Field names match exactly what AdminPage.tsx reads when approving a
// claim into venue_owners (owner_name, owner_role, owner_email,
// owner_phone, notes) — keep these two files in sync if either changes.
export interface ClaimSubmission {
  flow: 'claim_venue'
  venue_id: string
  venue_name: string
  owner_name: string
  owner_role: string
  owner_email: string
  owner_phone: string
  notes?: string
}

export type Contribution = NewVenueSubmission | EditSuggestion | ClaimSubmission

export interface SubmissionResult {
  success: boolean
  message: string
  id?: string
}

// ─────────────────────────────────────────────
// VALIDATION
// ─────────────────────────────────────────────

export interface ValidationError {
  field: string
  message: string
}

export function validateNewVenue(data: Partial<NewVenueSubmission>): ValidationError[] {
  const errors: ValidationError[] = []
  if (!data.name?.trim()) errors.push({ field: 'name', message: 'Venue name is required' })
  if (!data.neighborhood?.trim()) errors.push({ field: 'neighborhood', message: 'Neighborhood is required' })
  if (!data.deal_details?.trim() && !data.schedules?.length) {
    errors.push({ field: 'deal_details', message: 'Deal details are required' })
  }
  if (data.submitter_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.submitter_email)) {
    errors.push({ field: 'submitter_email', message: 'Invalid email address' })
  }
  return errors
}

export function validateEditSuggestion(data: Partial<EditSuggestion>): ValidationError[] {
  const errors: ValidationError[] = []
  if (!data.venue_id) errors.push({ field: 'venue_id', message: 'Venue ID is required' })
  if (!data.field_suggestions?.trim()) errors.push({ field: 'field_suggestions', message: 'Please describe what needs to change' })
  return errors
}

export function validateClaimSubmission(data: Partial<ClaimSubmission>): ValidationError[] {
  const errors: ValidationError[] = []
  if (!data.venue_id) errors.push({ field: 'venue_id', message: 'Venue ID is required' })
  if (!data.owner_name?.trim()) errors.push({ field: 'owner_name', message: 'Your name is required' })
  if (!data.owner_email?.trim() && !data.owner_phone?.trim()) {
    errors.push({ field: 'owner_email', message: 'Enter an email or phone number so we can reach you' })
  }
  if (data.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.owner_email)) {
    errors.push({ field: 'owner_email', message: 'Invalid email address' })
  }
  return errors
}

// ─────────────────────────────────────────────
// ADMIN NOTIFICATION
// ─────────────────────────────────────────────

async function notifyAdmin(contribution: Contribution): Promise<void> {
  try {
    let payload: any

    if (contribution.flow === 'new_venue') {
      const c = contribution as NewVenueSubmission
      payload = {
        venue_name: c.name,
        neighborhood: c.neighborhood,
        deal_details: c.deal_details || c.schedule_description,
        submitter_email: c.submitter_email,
        schedules: c.schedules,
      }
    } else if (contribution.flow === 'claim_venue') {
      const c = contribution as ClaimSubmission
      payload = {
        venue_name: `[CLAIM] ${c.venue_name}`,
        neighborhood: undefined,
        deal_details: `${c.owner_name} (${c.owner_role}) — ${c.owner_email || c.owner_phone}${c.notes ? ' — ' + c.notes : ''}`,
        submitter_email: c.owner_email,
        schedules: undefined,
      }
    } else {
      const c = contribution as EditSuggestion
      payload = {
        venue_name: c.venue_name,
        neighborhood: undefined,
        deal_details: c.field_suggestions,
        submitter_email: c.submitter_email,
        schedules: undefined,
      }
    }

    await fetch('/api/notify-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch {
    // Never fail the submission if the email fails
  }
}

// ─────────────────────────────────────────────
// SUBMIT
// ─────────────────────────────────────────────

export async function submitContribution(contribution: Contribution): Promise<SubmissionResult> {
    // Analytics.submissionStarted's parameter type predates the
    // claim_venue flow and still expects the old two-value union.
    // Analytics labels shouldn't be coupled to this domain type at all —
    // widening Analytics' signature to `string` in analytics.ts is the
    // correct long-term fix (safe: it's a logging label, not used for
    // branching logic), but that file isn't in hand to edit safely right
    // now. Cast here to unblock; flagged for cleanup.
    Analytics.submissionStarted(contribution.flow as 'new_venue' | 'suggest_edit')

  try {
    // ALL submissions — new venues, edit suggestions, and ownership
    // claims — go through the contributions table for admin review
    // before anything goes live or gets linked to a venue.
    const { data, error } = await supabase
      .from('contributions')
      .insert([{
        flow: contribution.flow,
        data: contribution,
        status: 'pending',
        created_at: new Date().toISOString(),
      }])
      .select('id')
      .single()

    if (error) throw error

    // Notify admin — fire and forget, don't await
    notifyAdmin(contribution)

    Analytics.submissionCompleted(
      contribution.flow as 'new_venue' | 'suggest_edit',
      contribution.flow !== 'new_venue' ? (contribution as EditSuggestion | ClaimSubmission).venue_id : undefined
    )

    const messages: Record<ContributionFlow, string> = {
      new_venue:    'Thanks! Your submission is under review and will go live once approved.',
      suggest_edit: 'Thanks for the correction! We will review and update the listing.',
      claim_venue:  'Thanks — we will verify and follow up with you soon.',
    }

    return {
      success: true,
      message: messages[contribution.flow],
      id: data?.id,
    }
  } catch (e: any) {
    console.error('submitContribution error:', e)
    return {
      success: false,
      message: 'Something went wrong. Please try again.',
    }
  }
}
